import React from "react";
import { Box, Typography } from "@mui/material";
import { colors } from "@ippon/shared-components";

interface Props {
  /** 明細合計 (税抜・円)。内訳テーブルの金額合計。 */
  subtotal: number;
  /** 消費税額 (円)。Σ(課税行 × 税率) の計算結果。 */
  taxAmount: number;
}

const yen = (n: number): string =>
  `¥${(Number.isFinite(n) ? n : 0).toLocaleString("ja-JP")}`;

const rowSx = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  py: 0.5,
} as const;

const labelSx = {
  fontSize: 13,
  color: colors.text.secondary,
} as const;

/**
 * 支出の金額サマリー (収入詳細 BillingItemsTable と同じ構成・表示専用)。
 *
 *   小計      ¥xxx   … 明細合計 (税抜)
 *   消費税    ¥xxx   … Σ(課税行 × 10%)
 *   ────────
 *   合計(税込) ¥xxx   … 小計 + 消費税 (太字)
 *
 * 「支払金額 / 税率 / 消費税」の手入力欄は廃止。税区分は内訳テーブルの各行で指定し、
 * 合計はここで自動算出して表示するだけ。
 */
const ExpenseAmountSummary: React.FC<Props> = ({ subtotal, taxAmount }) => {
  const total = subtotal + taxAmount;

  return (
    <Box
      sx={{
        backgroundColor: colors.background.lighter,
        borderRadius: "8px",
        p: 2,
      }}
    >
      <Box sx={rowSx}>
        <Typography sx={labelSx}>小計</Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.primary }}>
          {yen(subtotal)}
        </Typography>
      </Box>

      <Box sx={rowSx}>
        <Typography sx={labelSx}>消費税</Typography>
        <Typography sx={{ fontSize: 14, color: colors.text.primary }}>
          {yen(taxAmount)}
        </Typography>
      </Box>

      <Box sx={{ borderTop: `1px solid ${colors.border.light}`, my: 1 }} />

      <Box sx={rowSx}>
        <Typography
          sx={{ fontSize: 14, fontWeight: 700, color: colors.text.secondary }}
        >
          合計（税込）
        </Typography>
        <Typography
          sx={{ fontSize: 18, fontWeight: 700, color: colors.text.primary }}
        >
          {yen(total)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ExpenseAmountSummary;
